import { useEffect, useRef, useState } from "react";
import { Button, TextInput } from "@datapay-by-datacom/design-system";
import { MyPayEnvironmentSelect } from "../MyPayEnvironmentSelect";
import { useCompanyCodeValidation } from "../../../shared/hooks/useCompanyCodeValidation";
import type { MyPayEnvironment } from "../../../generated/core-api";
import s from "./CompanyContextStep.module.css";

export interface CompanyContext {
  mypayEnvironment: MyPayEnvironment;
  companyCode: string;
  companyName: string;
}

export type CompanyContextChange =
  | { type: "environment"; value: MyPayEnvironment | null }
  | { type: "companyCode"; value: string };

export interface CompanyContextStepProps {
  /** Prevent company and environment changes while the owning workflow is busy. */
  disabled?: boolean;
  /**
   * Optional interception point for a change that would clear a validated
   * context or validation error. The consumer may apply immediately, veto by
   * ignoring `applyChange`, or defer it until discard confirmation completes.
   */
  onContextChangeRequest?: (
    change: CompanyContextChange,
    applyChange: () => void
  ) => void;
  /**
   * Fired once validation succeeds. The context object holds the environment,
   * company code, and company name returned by the API.
   * @AC-CTX-02
   */
  onContextBound: (context: CompanyContext) => void;
  /**
   * Fired whenever the bind is dropped — environment change, code edit after
   * a result, or explicit reset.
   * @AC-CTX-06
   */
  onContextCleared: () => void;
  /**
   * Optional: notified when the environment selector changes (before
   * validation), so a consumer that needs the raw environment value (e.g.
   * to drive an invocation hook) can mirror it without waiting for a bind.
   */
  onEnvironmentChange?: (env: MyPayEnvironment | null) => void;
}

/**
 * Shared company context step for onboarding import flows.
 *
 * Composes the Datapay environment selector and company code input + Validate
 * button. Owns all environment, code, and validation state internally so
 * consumers only need to handle the bound/cleared callbacks.
 *
 * Consumers:
 *  - `WorkPatternImportInput` (work-patterns module, migrated in W3)
 *  - `PayComponentImportLayout` (pay-component-import module)
 *  - `SplitCostingImportLayout` (split-costing-import module, inline gate)
 *
 * ACs covered: CTX-01 (button disabled until env + code present),
 *   CTX-02 (success line format), CTX-03 (no-match copy via shared hook),
 *   CTX-04 (403 message via shared hook), CTX-06 (env change clears bind),
 *   CTX-07 (unvalidated code stays unbound).
 */
export function CompanyContextStep({
  disabled = false,
  onContextChangeRequest,
  onContextBound,
  onContextCleared,
  onEnvironmentChange,
}: CompanyContextStepProps) {
  const [mypayEnvironment, setMypayEnvironment] =
    useState<MyPayEnvironment | null>(null);
  const [companyCodeInput, setCompanyCodeInput] = useState("");

  // Hold onContextBound in a ref so the bind effect below depends only on the
  // bound values, not on the callback's identity. Consumers often pass a fresh
  // function each render; without this, the effect would re-run every render and
  // could loop (call → parent setState → re-render → new callback → effect again).
  const onContextBoundRef = useRef(onContextBound);
  onContextBoundRef.current = onContextBound;

  const {
    validatedCode,
    validatedEnvironment,
    companyName,
    isValidating: isValidatingCode,
    isValid: isCodeValid,
    validationError: codeValidationError,
    validateCode,
    reset: resetCode,
  } = useCompanyCodeValidation(mypayEnvironment);

  const handleEnvironmentChange = (env: MyPayEnvironment | null) => {
    if (disabled || env === mypayEnvironment) return;
    // The hook's useEffect already clears validation on env change. We update
    // the environment and notify the parent when a bind or error should drop
    // (R-CTX-6 / AC-CTX-06). The code input is left as-is so the user can
    // re-validate against the new environment without retyping.
    const applyChange = () => {
      setMypayEnvironment(env);
      onEnvironmentChange?.(env);
      onContextCleared();
    };
    if (isCodeValid || codeValidationError) {
      if (onContextChangeRequest) {
        onContextChangeRequest({ type: "environment", value: env }, applyChange);
      } else {
        applyChange();
      }
      return;
    }
    setMypayEnvironment(env);
    onEnvironmentChange?.(env);
  };

  const handleCompanyCodeChange = (value: string) => {
    if (disabled || value === companyCodeInput) return;
    // Drop the bind (or stale error) whenever the user edits the field.
    const applyChange = () => {
      setCompanyCodeInput(value);
      resetCode();
      onContextCleared();
    };
    if (isCodeValid || codeValidationError) {
      if (onContextChangeRequest) {
        onContextChangeRequest({ type: "companyCode", value }, applyChange);
      } else {
        applyChange();
      }
      return;
    }
    setCompanyCodeInput(value);
  };

  const handleValidateCode = () => {
    if (disabled) return;
    void validateCode(companyCodeInput);
  };

  // Fire onContextBound when validation returns a new valid code.
  // We derive the "just-became-valid" moment by checking whether the hook's
  // validatedCode is set AND matches the current env.
  const isBound =
    isCodeValid &&
    validatedCode !== null &&
    mypayEnvironment !== null &&
    validatedEnvironment === mypayEnvironment;

  // Notify the parent from an effect, never during render. Calling the parent
  // callback in the render body sets ancestor state mid-render; when the parent
  // stores a fresh object each time (e.g. the split-costing wizard context),
  // React can't bail out on reference equality, so the component re-renders,
  // fires the callback again, and loops forever — freezing the tab. The effect
  // only re-runs when the bound values actually change.
  useEffect(() => {
    if (
      isBound &&
      validatedCode !== null &&
      mypayEnvironment !== null &&
      companyName !== null
    ) {
      onContextBoundRef.current({
        mypayEnvironment,
        companyCode: validatedCode,
        companyName,
      });
    }
  }, [isBound, validatedCode, mypayEnvironment, companyName]);

  return (
    <div className={s.container}>
      <section
        className={s.environmentSection}
        aria-labelledby="company-ctx-env-heading"
      >
        <h3 id="company-ctx-env-heading" className={s.fieldHeading}>
          Datapay environment
        </h3>
        <MyPayEnvironmentSelect
          value={mypayEnvironment}
          onChange={handleEnvironmentChange}
          disabled={disabled}
        />
      </section>

      <section
        className={s.companyCodeSection}
        aria-labelledby="company-ctx-code-heading"
      >
        <h3 id="company-ctx-code-heading" className={s.fieldHeading}>
          Company code
        </h3>
        <div className={s.companyCodeRow}>
          <TextInput
            aria-label="Company Code"
            value={companyCodeInput}
            isRequired
            onChange={handleCompanyCodeChange}
            onInput={(evt) => handleCompanyCodeChange(evt.currentTarget.value)}
            onKeyDown={(evt) => {
              if (
                evt.key === "Enter" &&
                !disabled &&
                !isValidatingCode &&
                companyCodeInput.trim() &&
                mypayEnvironment
              ) {
                handleValidateCode();
              }
            }}
            isDisabled={disabled || isValidatingCode}
            isInvalid={!!codeValidationError}
            errorMessage={codeValidationError ?? undefined}
          />
          {/* @AC-CTX-01 — disabled until env selected AND code input non-empty */}
          <Button
            variant="secondary"
            size="large"
            aria-label="Validate company code"
            onPress={handleValidateCode}
            isPending={isValidatingCode}
            isDisabled={
              disabled ||
              isValidatingCode ||
              !companyCodeInput.trim() ||
              !mypayEnvironment
            }
          >
            Validate
          </Button>
        </div>

        {/* @AC-CTX-02 — success line: ✓ {companyName} ({code}) */}
        {isCodeValid && validatedCode && (
          <p className={s.companyCodeSuccess} role="status">
            <span aria-hidden="true">✓</span>{" "}
            {companyName
              ? `${companyName} (${validatedCode})`
              : validatedCode}
          </p>
        )}
      </section>
    </div>
  );
}
